import { useGetMyProfile, useGetMyNotificationPreferences, useUpdateMyNotificationPreferences, useVerifyTelegramWidget, useGetTelegramBotUsername, getGetMyProfileQueryKey, useRequestNotificationEmailChange, useVerifyNotificationEmailChange, useCreatePortalSession, useRegisterWhatsAppCommunity } from "@workspace/api-client-react";
import { getGetMyNotificationPreferencesQueryKey } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/react";
import { useState, useEffect, useRef } from "react";
import { useSearch } from "wouter";
import { Crown, Bell, Send, Mail, Lock, ExternalLink, CheckCircle2, RefreshCw, Pencil, X, Settings, Smartphone, ChevronDown, ChevronUp, Plus, Phone, Clock, AtSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { QRCodeSVG } from "qrcode.react";

declare function gtag(...args: unknown[]): void;

function TelegramLoginWidget({ botUsername, onAuth }: { botUsername: string; onAuth: (data: Record<string, unknown>) => void }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onAuthRef = useRef(onAuth);
  onAuthRef.current = onAuth;

  useEffect(() => {
    if (!containerRef.current) return;
    (window as any).__tgLoginCallback = (user: Record<string, unknown>) => onAuthRef.current(user);
    containerRef.current.innerHTML = "";
    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-onauth", "__tgLoginCallback(user)");
    script.setAttribute("data-request-access", "write");
    script.async = true;
    containerRef.current.appendChild(script);
    return () => { delete (window as any).__tgLoginCallback; };
  }, [botUsername]);

  return <div ref={containerRef} className="mt-1" />;
}


export default function DashboardPage() {
  const search = useSearch();
  const { user } = useUser();

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get("success") === "1" && typeof gtag !== "undefined") {
      const sessionId = params.get("session_id");
      gtag("event", "conversion", {
        send_to: "AW-18160060706/5WCrCL2196wcEKKSstND",
        value: 1.0,
        currency: "HKD",
        transaction_id: sessionId ?? undefined,
      });
    }
  }, [search]);
  const { data: profile, isLoading: profileLoading } = useGetMyProfile();
  const { data: notifPrefs, isLoading: notifLoading } = useGetMyNotificationPreferences({
    query: { enabled: !!profile, queryKey: getGetMyNotificationPreferencesQueryKey() }
  });
  const updateNotif = useUpdateMyNotificationPreferences();
  const verifyWidget = useVerifyTelegramWidget();
  const { data: botInfo } = useGetTelegramBotUsername();
  const queryClient = useQueryClient();
  const [showWidget, setShowWidget] = useState(false);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const [bindSuccess, setBindSuccess] = useState(false);

  const requestEmailChange = useRequestNotificationEmailChange();
  const verifyEmailChange = useVerifyNotificationEmailChange();
  const [emailEditMode, setEmailEditMode] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);

  const createPortal = useCreatePortalSession();
  const [portalLoading, setPortalLoading] = useState(false);

  const registerWhatsApp = useRegisterWhatsAppCommunity();
  const [whatsappMode, setWhatsappMode] = useState<"phone" | "username">("phone");
  const [whatsappPhoneInput, setWhatsappPhoneInput] = useState("");
  const [whatsappEditMode, setWhatsappEditMode] = useState(false);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);

  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setIsStandalone(window.matchMedia("(display-mode: standalone)").matches);
    const handler = (e: Event) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const isPaid = profile?.membershipTier === "paid";
  const loginEmail = user?.primaryEmailAddress?.emailAddress ?? profile?.email ?? "";

  const periodEnd = profile?.subscriptionCurrentPeriodEnd
    ? new Date(profile.subscriptionCurrentPeriodEnd)
    : null;
  const periodEndStr = periodEnd
    ? `${periodEnd.getFullYear()}年${periodEnd.getMonth() + 1}月${periodEnd.getDate()}日`
    : null;

  const handleManageSubscription = () => {
    setPortalLoading(true);
    createPortal.mutate(undefined, {
      onSuccess: (data) => { window.location.href = data.url; },
      onError: () => setPortalLoading(false),
    });
  };

  const handleWidgetAuth = (data: Record<string, unknown>) => {
    setWidgetError(null);
    verifyWidget.mutate(
      { data: data as any },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() });
          setShowWidget(false);
          setBindSuccess(true);
          setTimeout(() => setBindSuccess(false), 4000);
        },
        onError: () => setWidgetError("綁定失敗，請重試"),
      }
    );
  };

  const handleToggleEmailNotif = (checked: boolean) => {
    updateNotif.mutate(
      { data: { enableEmailNotifications: checked } },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: getGetMyNotificationPreferencesQueryKey() }) }
    );
  };

  const handleRequestEmailChange = () => {
    setEmailError(null);
    requestEmailChange.mutate(
      { data: { newEmail } },
      {
        onSuccess: () => { setOtpSent(true); setEmailError(null); },
        onError: (err: any) => setEmailError(err?.response?.data?.error ?? "發送失敗，請重試"),
      }
    );
  };

  const handleVerifyEmailChange = () => {
    setEmailError(null);
    verifyEmailChange.mutate(
      { data: { otp } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMyNotificationPreferencesQueryKey() });
          setEmailEditMode(false);
          setOtpSent(false);
          setNewEmail("");
          setOtp("");
          setEmailSuccess(true);
          setTimeout(() => setEmailSuccess(false), 4000);
        },
        onError: (err: any) => setEmailError(err?.response?.data?.error ?? "驗證失敗，請重試"),
      }
    );
  };

  const handleRegisterWhatsApp = () => {
    setWhatsappError(null);
    const value = whatsappPhoneInput.trim();

    if (whatsappMode === "phone") {
      if (!/^[+\d][\d\s-]{6,19}$/.test(value)) {
        setWhatsappError("請輸入有效的 WhatsApp 電話號碼（包含國碼，例如 +852xxxxxxxx）");
        return;
      }
    } else {
      const username = value.replace(/^@/, "");
      if (!/^[a-zA-Z][a-zA-Z0-9._]{3,59}$/.test(username)) {
        setWhatsappError("請輸入有效的 WhatsApp @username（4-60 個字元，以英文字母開頭）");
        return;
      }
    }

    const data = whatsappMode === "phone"
      ? { phone: value }
      : { username: value.replace(/^@/, "") };

    registerWhatsApp.mutate(
      { data },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getGetMyProfileQueryKey() });
          setWhatsappEditMode(false);
          setWhatsappPhoneInput("");
        },
        onError: (err: any) => setWhatsappError(err?.response?.data?.error ?? "登記失敗，請重試"),
      }
    );
  };

  const handleCancelEmailEdit = () => {
    setEmailEditMode(false);
    setOtpSent(false);
    setNewEmail("");
    setOtp("");
    setEmailError(null);
  };

  if (profileLoading) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-3xl">
        <Skeleton className="h-8 w-48 mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b bg-secondary/30 py-8">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <h1 className="text-2xl font-bold mb-1">控制台</h1>
          <p className="text-muted-foreground text-sm">管理您的帳戶設定及通知偏好</p>
        </div>
      </div>
      <div className="container mx-auto px-4 md:px-8 py-8 max-w-3xl space-y-6">

        {/* Membership Status */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`p-2 rounded-lg shrink-0 ${isPaid ? "bg-amber-50 text-amber-500" : "bg-secondary text-muted-foreground"}`}>
                  <Crown className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">會員狀態</div>
                  <div className="text-xs text-muted-foreground truncate">{profile?.email}</div>
                  {isPaid && periodEndStr && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      訂閱到期：{periodEndStr}
                    </div>
                  )}
                  {isPaid && !periodEndStr && (
                    <div className="text-xs text-amber-500 font-medium mt-0.5">
                      訂閱狀態：{profile?.subscriptionStatus ?? "有效"}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={isPaid ? "bg-amber-50 text-amber-500 border-amber-300" : ""} variant={isPaid ? "outline" : "secondary"}>
                  {isPaid ? "付費會員" : "免費會員"}
                </Badge>
                {isPaid ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5"
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                  >
                    <Settings className="h-3.5 w-3.5" />
                    {portalLoading ? "載入中…" : "管理訂閱"}
                  </Button>
                ) : (
                  <Link href="/pricing">
                    <Button size="sm" data-testid="button-upgrade">升級計劃</Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="h-4 w-4 text-primary" />
              電郵通知設定
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {notifLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <>
                {/* Toggle — available to ALL members */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">啟用每日電郵通知</p>
                    <p className="text-xs text-muted-foreground">每日發送最新招標項目至您的登入電郵</p>
                  </div>
                  <Switch
                    checked={notifPrefs?.enableEmailNotifications ?? true}
                    onCheckedChange={handleToggleEmailNotif}
                    disabled={updateNotif.isPending}
                    data-testid="switch-email-notifications"
                  />
                </div>

                {/* Notification email — editable with OTP */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">通知電郵地址</label>

                  {!emailEditMode ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md bg-secondary/60 border border-border min-w-0">
                        <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="text-sm text-[#677e73] truncate">
                          {notifPrefs?.notificationEmail ?? loginEmail}
                        </span>
                        {(!!notifPrefs?.notificationEmail || !loginEmail.endsWith("@privaterelay.appleid.com")) && (
                          <span className="ml-auto text-xs text-muted-foreground shrink-0">（不可更改）</span>
                        )}
                      </div>
                      {loginEmail.endsWith("@privaterelay.appleid.com") && !notifPrefs?.notificationEmail && (
                        <Button
                          variant="outline" size="sm"
                          className="shrink-0 gap-1.5"
                          onClick={() => { setEmailEditMode(true); setEmailError(null); }}
                        >
                          <Pencil className="h-3.5 w-3.5" /> 修改
                        </Button>
                      )}
                    </div>
                  ) : !otpSent ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        輸入新的通知電郵地址，系統將發送驗證碼至該地址確認。不影響登入帳號。
                      </p>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="新通知電郵地址"
                          value={newEmail}
                          onChange={e => setNewEmail(e.target.value)}
                          className="text-sm h-9"
                          onKeyDown={e => { if (e.key === "Enter") handleRequestEmailChange(); }}
                        />
                        <Button
                          size="sm" className="shrink-0 h-9"
                          onClick={handleRequestEmailChange}
                          disabled={requestEmailChange.isPending || !newEmail.trim()}
                        >
                          {requestEmailChange.isPending ? "發送中…" : "發送驗證碼"}
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="shrink-0 h-9"
                          onClick={handleCancelEmailEdit}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                        驗證碼已發送至 <strong>{newEmail}</strong>，請在 10 分鐘內輸入。
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="6 位驗證碼"
                          value={otp}
                          onChange={e => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          className="text-sm h-9 tracking-widest font-mono w-36"
                          maxLength={6}
                          onKeyDown={e => { if (e.key === "Enter") handleVerifyEmailChange(); }}
                        />
                        <Button
                          size="sm" className="shrink-0 h-9"
                          onClick={handleVerifyEmailChange}
                          disabled={verifyEmailChange.isPending || otp.length !== 6}
                        >
                          {verifyEmailChange.isPending ? "驗證中…" : "確認修改"}
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="shrink-0 h-9 text-xs"
                          onClick={() => { setOtpSent(false); setOtp(""); setEmailError(null); }}
                        >
                          重新發送
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="shrink-0 h-9"
                          onClick={handleCancelEmailEdit}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                    </div>
                  )}

                  {emailSuccess && (
                    <div className="flex items-center gap-2 text-xs text-primary">
                      <CheckCircle2 className="h-3.5 w-3.5" /> 通知電郵已成功更新
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    登入帳號：{loginEmail}（不受影響）
                  </p>
                </div>

              </>
            )}
          </CardContent>
        </Card>

        {/* Telegram */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <img src="/telegram-logo.webp" alt="Telegram" width={32} height={32} />
              Telegram 頻道
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Public channel — all members */}
            <div className="p-3 rounded-lg bg-secondary/40 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium">📋 招標頻道</p>
                  <p className="text-xs text-muted-foreground">免費公開頻道，每日最新學校招標基本資訊</p>
                </div>
                <a href="https://t.me/Skoltend" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    加入 <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://t.me/Skoltend" target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded-lg overflow-hidden border border-border bg-white p-1.5 hover:opacity-80 transition-opacity">
                  <img src="/qrcode-telegram.jpg" alt="Telegram QR Code" width={88} height={88} className="rounded" />
                </a>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  掃描 QR Code 或點擊「加入」按鈕，即可加入免費招標頻道，每日接收最新學校招標資訊。
                </p>
              </div>
            </div>

            {/* Member channel — paid only */}
            <div className={`p-3 rounded-lg border ${isPaid ? "bg-primary/5 border-primary/20" : "bg-secondary/30 border-border opacity-60"}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    🔑 付費會員頻道
                    {!isPaid && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                  </p>
                  <p className="text-xs text-muted-foreground">完整招標資訊，限付費會員</p>
                </div>
                {isPaid && (
                  profile?.telegramId ? (
                    <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 gap-1">
                      <CheckCircle2 className="h-3 w-3" /> 已綁定
                    </Badge>
                  ) : null
                )}
              </div>

              {isPaid && (
                bindSuccess ? (
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <CheckCircle2 className="h-4 w-4" /> 綁定成功！Bot 將發送頻道邀請至你的 Telegram。
                  </div>
                ) : profile?.telegramId && !showWidget ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background border border-border text-sm text-blue-600 font-medium">
                      {profile.telegramUsername ?? `ID: ${profile.telegramId}`}
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setShowWidget(true)}
                      className="gap-1.5"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> 重新綁定
                    </Button>
                  </div>
                ) : showWidget || !profile?.telegramId ? (
                  <div className="space-y-3">
                    <ol className="text-xs text-muted-foreground space-y-1 list-none">
                      <li className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center text-[10px]">1</span>
                        點擊下方「以 Telegram 登入」按鈕，授權你的 Telegram 帳號
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center text-[10px]">2</span>
                        系統完成綁定後，Skoltend CS 會向你發送付費頻道邀請連結
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/15 text-primary font-semibold flex items-center justify-center text-[10px]">3</span>
                        點擊連結，即可加入付費會員 Telegram 頻道
                      </li>
                    </ol>
                    {showWidget && (
                      <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 space-y-1">
                        <p>按鈕會自動使用你<strong>瀏覽器目前已授權</strong>的 Telegram 帳號。</p>
                        <p>如需更換帳號，請使用<strong>無痕視窗</strong>或另一個瀏覽器開啟此頁面，再進行綁定。</p>
                      </div>
                    )}
                    {botInfo?.username ? (
                      <TelegramLoginWidget botUsername={botInfo.username} onAuth={handleWidgetAuth} />
                    ) : (
                      <Skeleton className="h-10 w-32 rounded-md" />
                    )}
                    {verifyWidget.isPending && <p className="text-xs text-muted-foreground">驗證中…</p>}
                    {widgetError && <p className="text-xs text-red-500">{widgetError}</p>}
                    {showWidget && (
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowWidget(false)}>取消</Button>
                    )}
                  </div>
                ) : null
              )}

              {!isPaid && (
                <Link href="/pricing">
                  <Button size="sm" variant="outline" className="mt-1">升級付費會員</Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* WhatsApp */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <img src="/whatsapp-logo.webp" alt="WhatsApp" width={32} height={32} />
              WhatsApp 社群
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            {/* Public community — all members */}
            <div className="p-3 rounded-lg bg-secondary/40 border border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium">📋 招標公告社群</p>
                  <p className="text-xs text-muted-foreground">免費公開社群，每日最新學校招標基本資訊</p>
                </div>
                <a href="https://chat.whatsapp.com/BUf6yqbQJt3Eix9YUVa1jl" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1.5">
                    加入 <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://chat.whatsapp.com/BUf6yqbQJt3Eix9YUVa1jl" target="_blank" rel="noopener noreferrer" className="shrink-0 block rounded-lg overflow-hidden border border-border bg-white p-1.5 hover:opacity-80 transition-opacity">
                  <img src="/qrcode-whatsapp-20260915.png" alt="WhatsApp QR Code" width={88} height={88} className="rounded" />
                </a>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  掃描 QR Code 或點擊「加入」按鈕，即可加入免費招標公告社群，每日接收最新學校招標資訊。
                </p>
              </div>
            </div>

            {/* Member community — registration + admin confirmation */}
            <div className={`p-3 rounded-lg border ${isPaid ? "bg-primary/5 border-primary/20" : "bg-secondary/30 border-border opacity-60"}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    🔑 付費會員社群
                    {!isPaid && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                  </p>
                  <p className="text-xs text-muted-foreground">完整招標資訊，限付費會員</p>
                </div>
                {isPaid && profile?.whatsappRequestStatus === "confirmed" && (
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 gap-1">
                    <CheckCircle2 className="h-3 w-3" /> 已確認
                  </Badge>
                )}
                {isPaid && profile?.whatsappRequestStatus === "pending" && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-300 gap-1">
                    <Clock className="h-3 w-3" /> 審核中
                  </Badge>
                )}
              </div>

              {!isPaid && (
                <Link href="/pricing">
                  <Button size="sm" variant="outline" className="mt-1">升級付費會員</Button>
                </Link>
              )}

              {isPaid && profile?.whatsappRequestStatus === "confirmed" && !whatsappEditMode && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-primary">
                    <CheckCircle2 className="h-4 w-4" /> 已加入付費會員 WhatsApp 社群
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background border border-border text-sm text-blue-600 font-medium">
                    {profile.whatsappUsername
                      ? <><AtSign className="h-3.5 w-3.5 shrink-0" /> {profile.whatsappUsername}</>
                      : <><Phone className="h-3.5 w-3.5 shrink-0" /> {profile.whatsappPhone}</>}
                  </div>
                  <Button
                    variant="outline" size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      setWhatsappEditMode(true);
                      if (profile.whatsappUsername) {
                        setWhatsappMode("username");
                        setWhatsappPhoneInput(profile.whatsappUsername);
                      } else {
                        setWhatsappMode("phone");
                        setWhatsappPhoneInput(profile.whatsappPhone ?? "");
                      }
                      setWhatsappError(null);
                    }}
                  >
                    <RefreshCw className="h-3.5 w-3.5" /> 重新綁定
                  </Button>
                </div>
              )}

              {isPaid && profile?.whatsappRequestStatus === "pending" && !whatsappEditMode && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    已登記{profile.whatsappUsername ? "帳號" : "電話號碼"}，我們將盡快將你加入付費會員 WhatsApp 社群，請留意 WhatsApp 訊息。
                  </p>
                  <p className="text-xs text-amber-600 leading-relaxed">
                    會員審批可能需要 1-2 個工作天，請耐心等候。
                  </p>
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-background border border-border text-sm text-muted-foreground">
                    {profile.whatsappUsername
                      ? <><AtSign className="h-3.5 w-3.5 shrink-0" /> {profile.whatsappUsername}</>
                      : <><Phone className="h-3.5 w-3.5 shrink-0" /> {profile.whatsappPhone}</>}
                  </div>
                </div>
              )}

              {isPaid && (!profile?.whatsappRequestStatus || whatsappEditMode) && (
                <div className="space-y-2">
                  <div className="inline-flex rounded-md border border-border p-0.5 bg-secondary/40">
                    <button
                      type="button"
                      className={`px-3 py-1 text-xs rounded-sm transition-colors ${whatsappMode === "phone" ? "bg-primary text-primary-foreground shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
                      onClick={() => { setWhatsappMode("phone"); setWhatsappPhoneInput(""); setWhatsappError(null); }}
                    >
                      電話號碼
                    </button>
                    <button
                      type="button"
                      disabled={whatsappMode !== "username"}
                      title="WhatsApp @username 功能尚未正式推行，暫不開放"
                      className={`px-3 py-1 text-xs rounded-sm transition-colors ${whatsappMode === "username" ? "bg-primary text-primary-foreground shadow-sm font-medium" : "text-muted-foreground/40 cursor-not-allowed"}`}
                    >
                      @username
                    </button>
                  </div>
                  {whatsappMode !== "username" && (
                    <p className="text-xs text-amber-600 leading-relaxed">
                      WhatsApp @username 登記功能尚未開放，有待 WhatsApp 正式推行帳號功能後，本站才會開放使用，暫時請以電話號碼登記。
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {whatsappMode === "phone"
                      ? "請輸入你的 WhatsApp 電話號碼（包含國碼）以登記加入付費會員社群，我們確認後會將你加入社群。"
                      : "請輸入你的 WhatsApp @username 以登記加入付費會員社群，我們確認後會將你加入社群。"}
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type={whatsappMode === "phone" ? "tel" : "text"}
                      placeholder={whatsappMode === "phone" ? "例如 +85261234567" : "例如 @johntan"}
                      value={whatsappPhoneInput}
                      onChange={e => setWhatsappPhoneInput(e.target.value)}
                      className="text-sm h-9 bg-white placeholder:text-muted-foreground/40"
                      onKeyDown={e => { if (e.key === "Enter") handleRegisterWhatsApp(); }}
                    />
                    <Button
                      size="sm" className="shrink-0 h-9"
                      onClick={handleRegisterWhatsApp}
                      disabled={registerWhatsApp.isPending || !whatsappPhoneInput.trim()}
                    >
                      {registerWhatsApp.isPending ? "登記中…" : "登記"}
                    </Button>
                    {whatsappEditMode && (
                      <Button
                        size="sm" variant="ghost" className="shrink-0 h-9"
                        onClick={() => { setWhatsappEditMode(false); setWhatsappPhoneInput(""); setWhatsappError(null); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {whatsappError && <p className="text-xs text-red-500">{whatsappError}</p>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add to Home Screen */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-primary" />
              加入主畫面
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isStandalone && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <CheckCircle2 className="h-4 w-4" />
                已加入主畫面，可像 App 般使用
              </div>
            )}

            {/* Android: native install prompt */}
            {!isIos && deferredPrompt && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">點擊下方按鈕，瀏覽器將彈出安裝提示，將 Skoltend 加入主畫面。</p>
                <Button size="sm" className="gap-1.5" onClick={handleAndroidInstall}>
                  <Plus className="h-4 w-4" /> 加入主畫面
                </Button>
              </div>
            )}

            {/* iOS or desktop without prompt: show step guide */}
            {(isIos || (!deferredPrompt && !isStandalone)) && (
              <div className="space-y-3">
                {!isStandalone && (
                  <p className="text-xs text-muted-foreground">
                    {isIos
                      ? "將 Skoltend 加入 iPhone／iPad 主畫面，即可像 App 般快速開啟。"
                      : "在手機上將 Skoltend 加入主畫面，即可像 App 般快速開啟。"}
                  </p>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setShowIosGuide(!showIosGuide)}
                >
                  {showIosGuide ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showIosGuide ? "收起步驟" : "查看操作步驟"}
                </Button>
                {showIosGuide && (
                  <ol className="space-y-2 pt-1">
                    {[
                      <>在 <strong>Safari</strong> 或 <strong>Chrome</strong> 瀏覽器開啟 <strong>skoltend.com</strong></>,
                      <>點擊底部（iOS）或右上角（Android）的「<strong>共享</strong>」／選單按鈕</>,
                      <>在選單中選擇「<strong>加至主畫面</strong>」／「<strong>Add to Home Screen</strong>」</>,
                      <>點擊「<strong>加入</strong>」完成，主畫面即出現 Skoltend 圖示</>,
                    ].map((step, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <span className="text-foreground leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
